import { auth, seed, settings as settingsApi, emailIngestion } from '../api';
import { getCurrentUser, checkAuth } from '../auth';
import { createPageHeader } from '../components/form';
import { showModal, confirmModal } from '../components/modal';
import { showToast } from '../components/toast';
import type { SettingsInfo } from '../types';

export async function settingsPage(): Promise<HTMLElement> {
  const page = document.createElement('div');
  page.appendChild(createPageHeader('Settings'));

  const user = getCurrentUser();
  const isOwner = user?.role === 'owner';

  // Load settings info async
  let settingsInfo: SettingsInfo | null = null;
  try {
    settingsInfo = await settingsApi.info();
  } catch { /* show fallback */ }

  const notifPref = settingsInfo?.notification_preference || user?.notification_preference || 'email';
  const expectedEmails = settingsInfo?.expected_sender_emails || user?.expected_sender_emails || [];
  const smsNumber = settingsInfo?.sms_number || 'Not configured';
  const imapConfigured = settingsInfo?.imap_configured || false;
  const propertymeUsername = settingsInfo?.propertyme_username || '';

  page.innerHTML += `
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Profile</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between"><dt class="text-gray-500">Username</dt><dd class="font-medium">${user?.username || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Name</dt><dd class="font-medium">${user?.name || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Email</dt><dd class="font-medium">${user?.email || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Phone</dt><dd class="font-medium">${user?.phone || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Role</dt><dd><span class="badge badge-${isOwner ? 'green' : 'blue'}">${user?.role || '—'}</span></dd></div>
        </dl>
      </div>

      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Account Management</h3>
        <p class="text-sm text-gray-500 mb-4">Update your username, email, or password. Current password is required for all changes.</p>
        <form id="account-form" class="space-y-4">
          <div>
            <label class="label">New Username</label>
            <input name="new_username" type="text" class="input-field" placeholder="${user?.username || ''}" />
          </div>
          <div>
            <label class="label">New Email</label>
            <input name="email" type="email" class="input-field" placeholder="${user?.email || ''}" />
          </div>
          <div>
            <label class="label">New Password</label>
            <input name="new_password" type="password" class="input-field" placeholder="Leave empty to keep current" />
          </div>
          <div>
            <label class="label">Confirm New Password</label>
            <input name="confirm_password" type="password" class="input-field" placeholder="Confirm new password" />
          </div>
          <hr class="border-gray-200" />
          <div>
            <label class="label">Current Password <span class="text-red-500">*</span></label>
            <input name="current_password" type="password" class="input-field" required />
          </div>
          <button type="submit" class="btn-primary">Update Account</button>
        </form>
      </div>

      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Notification Preferences</h3>
        <form id="notif-form" class="space-y-4">
          <div>
            <label class="label">Notification Channel</label>
            <select name="notification_preference" class="input-field">
              <option value="email" ${notifPref === 'email' ? 'selected' : ''}>Email only</option>
              <option value="sms" ${notifPref === 'sms' ? 'selected' : ''}>SMS only</option>
              <option value="both" ${notifPref === 'both' ? 'selected' : ''}>Both Email &amp; SMS</option>
            </select>
          </div>
          <div>
            <label class="label">Expected Sender Emails</label>
            <p class="text-xs text-gray-400 mb-2">Emails from these addresses will be automatically matched to you. One per line.</p>
            <textarea name="expected_sender_emails" class="input-field" rows="3" placeholder="agent@example.com&#10;strata@example.com">${expectedEmails.join('\n')}</textarea>
          </div>
          <button type="submit" class="btn-primary">Save Preferences</button>
        </form>
      </div>

      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Email Ingestion (IMAP)</h3>
        <p class="text-sm text-gray-500 mb-4">The system polls emails via IMAP automatically every hour. You can also trigger a manual poll.</p>
        <dl class="space-y-4 text-sm">
          <div class="flex justify-between">
            <dt class="text-gray-500">IMAP Status</dt>
            <dd>${imapConfigured
      ? '<span class="badge badge-green">Configured</span>'
      : '<span class="badge badge-yellow">Not configured</span>'}</dd>
          </div>
          <div class="flex justify-between">
            <dt class="text-gray-500">SMS Number</dt>
            <dd><code class="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-mono">${smsNumber}</code></dd>
          </div>
        </dl>
        ${!imapConfigured ? `
        <div class="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
          <p class="font-medium text-blue-800 mb-2">To configure IMAP, set these environment variables:</p>
          <ul class="space-y-1 text-blue-700 font-mono text-xs">
            <li>IMAP_SERVER=<span class="text-blue-500">imap.gmail.com</span></li>
            <li>IMAP_USERNAME=<span class="text-blue-500">your@email.com</span></li>
            <li>IMAP_PASSWORD=<span class="text-blue-500">your-app-password</span></li>
            <li>IMAP_PORT=<span class="text-blue-500">993</span></li>
            <li>IMAP_USE_SSL=<span class="text-blue-500">true</span></li>
          </ul>
        </div>` : ''}
        <div class="mt-4 flex gap-3">
          <button class="btn-primary" id="poll-emails-btn">Poll Emails Now</button>
        </div>
        <div id="poll-result" class="mt-3 hidden">
          <div class="bg-gray-50 rounded-lg p-3 text-sm"></div>
        </div>
      </div>

      ${isOwner ? `
      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Invite User</h3>
        <p class="text-sm text-gray-500 mb-4">Invite an associate to view your property portfolio.</p>
        <form id="invite-form" class="space-y-3">
          <div><label class="label">Email</label><input name="email" type="email" class="input-field" required></div>
          <div><label class="label">Name</label><input name="name" type="text" class="input-field" required></div>
          <div><label class="label">Role</label>
            <select name="role" class="input-field">
              <option value="associate">Associate</option>
            </select>
          </div>
          <button type="submit" class="btn-primary">Send Invite</button>
        </form>
      </div>
      ` : ''}

      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">PropertyMe Sync</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between items-center">
            <dt class="text-gray-500">Saved Username</dt>
            <dd class="font-medium">${propertymeUsername
      ? `<span id="pm-username-display">${propertymeUsername}</span>`
      : '<span class="text-gray-400">Not saved</span>'}</dd>
          </div>
        </dl>
        ${propertymeUsername ? `
        <div class="mt-4">
          <button class="btn-outline text-red-600 hover:bg-red-50 border-red-200 text-sm" id="clear-pm-username-btn">Clear Saved Username</button>
        </div>` : `
        <p class="text-xs text-gray-400 mt-3">Your PropertyMe username will be saved automatically after a successful sync.</p>`}
      </div>

      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Demo Data</h3>
        <p class="text-sm text-gray-500 mb-4">Seed the database with sample properties, payments, and maintenance data for testing.</p>
        <div class="flex gap-3 flex-wrap">
          <button class="btn-outline" id="seed-btn">Load Demo Data</button>
          <button class="btn-outline text-red-600 hover:bg-red-50 border-red-200" id="purge-demo-btn">Purge Demo Data</button>
        </div>
      </div>

      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">About</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between"><dt class="text-gray-500">Version</dt><dd class="font-medium">2.0.0</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Backend</dt><dd class="font-medium">Azure Functions v4 (Python)</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Frontend</dt><dd class="font-medium">TypeScript + Tailwind CSS</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Storage</dt><dd class="font-medium">Azure Table + Blob Storage</dd></div>
        </dl>
      </div>
    </div>
  `;

  // Wire event handlers
  setTimeout(() => {
    // Account management form
    const accountForm = page.querySelector('#account-form') as HTMLFormElement | null;
    if (accountForm) {
      accountForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(accountForm);
        const currentPassword = (fd.get('current_password') as string) || '';
        const newUsername = (fd.get('new_username') as string)?.trim() || '';
        const email = (fd.get('email') as string)?.trim() || '';
        const newPassword = (fd.get('new_password') as string) || '';
        const confirmPassword = (fd.get('confirm_password') as string) || '';

        if (!currentPassword) {
          showToast('Current password is required', 'error');
          return;
        }
        if (!newUsername && !email && !newPassword) {
          showToast('No changes to save', 'info');
          return;
        }
        if (newPassword && newPassword !== confirmPassword) {
          showToast('New passwords do not match', 'error');
          return;
        }
        if (newPassword && newPassword.length < 8) {
          showToast('New password must be at least 8 characters', 'error');
          return;
        }

        const payload: Record<string, string> = { current_password: currentPassword };
        if (newUsername) payload.new_username = newUsername;
        if (email) payload.email = email;
        if (newPassword) payload.new_password = newPassword;

        const btn = accountForm.querySelector('button[type="submit"]') as HTMLButtonElement;
        btn.disabled = true;
        btn.textContent = 'Updating...';
        try {
          const result = await auth.updateProfile(payload);
          // Store new token
          localStorage.setItem('token', result.token);
          // Refresh auth state
          await checkAuth();
          showToast('Account updated successfully!', 'success');
          // Reload page to reflect changes
          setTimeout(() => window.location.reload(), 500);
        } catch (err: unknown) {
          let msg = 'Failed to update account';
          try {
            const parsed = JSON.parse((err as Error).message || '{}');
            if (parsed.error) msg = parsed.error;
          } catch { /* use default */ }
          showToast(msg, 'error');
        }
        btn.disabled = false;
        btn.textContent = 'Update Account';
      });
    }

    // Notification preferences form
    const notifForm = page.querySelector('#notif-form') as HTMLFormElement | null;
    if (notifForm) {
      notifForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(notifForm);
        const emailsText = (fd.get('expected_sender_emails') as string) || '';
        const emailList = emailsText.split('\n').map(s => s.trim()).filter(Boolean);
        try {
          await settingsApi.update({
            notification_preference: fd.get('notification_preference') as string,
            expected_sender_emails: emailList,
          });
          showToast('Preferences saved!', 'success');
        } catch (err: unknown) {
          showToast((err as Error).message || 'Failed to save', 'error');
        }
      });
    }

    // Poll emails button
    page.querySelector('#poll-emails-btn')?.addEventListener('click', async () => {
      const btn = page.querySelector('#poll-emails-btn') as HTMLButtonElement;
      const result = page.querySelector('#poll-result') as HTMLElement;
      btn.disabled = true;
      btn.textContent = 'Polling...';
      try {
        const res = await emailIngestion.poll();
        result.classList.remove('hidden');
        result.querySelector('div')!.innerHTML = `
          <span class="text-green-600 font-medium">Poll complete.</span> 
          Processed: ${res.processed}, Errors: ${res.errors}
        `;
      } catch (err: any) {
        result.classList.remove('hidden');
        let msg = 'Poll failed. Check IMAP settings.';
        try {
          const parsed = JSON.parse(err.message || '{}');
          if (parsed.error) msg = parsed.error;
        } catch { /* use default */ }
        result.querySelector('div')!.innerHTML = `<span class="text-red-500">${msg}</span>`;
      }
      btn.disabled = false;
      btn.textContent = 'Poll Emails Now';
    });

    // Invite form handler
    const inviteForm = page.querySelector('#invite-form') as HTMLFormElement | null;
    if (inviteForm) {
      inviteForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(inviteForm);
        try {
          await auth.invite({
            email: fd.get('email') as string,
            name: fd.get('name') as string,
            role: fd.get('role') as string,
          });
          showToast('Invite sent!', 'success');
          inviteForm.reset();
        } catch (err: unknown) {
          showToast((err as Error).message || 'Failed to send invite', 'error');
        }
      });
    }

    // Clear PropertyMe username
    page.querySelector('#clear-pm-username-btn')?.addEventListener('click', async () => {
      const btn = page.querySelector('#clear-pm-username-btn') as HTMLButtonElement;
      btn.disabled = true;
      try {
        await settingsApi.update({ propertyme_username: '' });
        showToast('PropertyMe username cleared', 'success');
        page.querySelector('#pm-username-display')?.closest('dd')?.replaceWith(
          Object.assign(document.createElement('dd'), { innerHTML: '<span class="text-gray-400">Not saved</span>' })
        );
        btn.closest('div')?.remove();
      } catch (err: unknown) {
        showToast((err as Error).message || 'Failed to clear username', 'error');
        btn.disabled = false;
      }
    });

    page.querySelector('#seed-btn')?.addEventListener('click', async () => {
      const btn = page.querySelector('#seed-btn') as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'Loading...';
      try {
        const result = await seed.run();
        showToast(result.message || 'Demo data loaded!', 'success');
      } catch { showToast('Failed to load demo data', 'error'); }
      btn.disabled = false;
      btn.textContent = 'Load Demo Data';
    });

    page.querySelector('#purge-demo-btn')?.addEventListener('click', async () => {
      const btn = page.querySelector('#purge-demo-btn') as HTMLButtonElement;
      const confirmed = await confirmModal('Are you sure you want to delete all demo data? This cannot be undone.');
      if (!confirmed) return;
      btn.disabled = true;
      btn.textContent = 'Purging...';
      try {
        const result = await seed.purgeDemo();
        showToast(result.message || 'Demo data purged', 'success');
        setTimeout(() => window.location.reload(), 1000);
      } catch (err: unknown) {
        showToast((err as Error).message || 'Failed to purge demo data', 'error');
      }
      btn.disabled = false;
      btn.textContent = 'Purge Demo Data';
    });
  });

  return page;
}
